<template>
  <main class="monitoring-page">
    <div class="toolbar">
      <button
        v-for="opt in rangeOptions"
        :key="opt.key"
        class="btn"
        :class="{ primary: range === opt.key }"
        @click="switchRange(opt.key)"
      >{{ opt.label }}</button>
      <button class="btn" @click="exportSnapshot">导出监控快照</button>
    </div>

    <div class="grid kpis four" style="margin-top:0">
      <div class="card" v-for="(card, idx) in summaryCards" :key="idx">
        <div class="card-head">
          <span>{{ card.title }}</span>
          <span>LIVE</span>
        </div>
        <div class="metric">{{ card.value }}<small>{{ card.unit }}</small></div>
        <div class="delta" :class="card.changeClass">
          {{ card.changeArrow }} {{ card.changeText }} {{ compareLabel }}
        </div>
      </div>
    </div>

    <div class="grid two">
      <div class="panel">
        <div class="panel-head">
          <h3 class="panel-title">请求趋势</h3>
          <span class="panel-sub">{{ rangeLabel }}</span>
        </div>
        <div ref="requestTrendChart" class="chart-container"></div>
        <div v-if="requestTrendMeta" class="trend-meta">
          <span><b>{{ requestTrendMeta.peak }}</b>峰值</span>
          <span><b>{{ requestTrendMeta.current }}</b>当前</span>
          <span><b>{{ requestTrendMeta.change }}</b>环比</span>
        </div>
      </div>
      <div class="panel">
        <div class="panel-head">
          <h3 class="panel-title">延迟分布</h3>
          <span class="panel-sub">近 1 小时</span>
        </div>
        <div ref="latencyChart" class="chart-container"></div>
        <div v-if="latencySummary" class="latency-summary">
          <span><b>{{ latencySummary.mainPct }}</b>{{ latencySummary.mainLabel }}</span>
          <span><b>{{ latencySummary.slow }}</b>慢请求</span>
          <span><b>{{ latencySummary.timeout }}</b>超时需治理</span>
        </div>
      </div>
    </div>

    <div class="grid two" style="margin-top:16px">
      <div class="panel">
        <div class="panel-head">
          <h3 class="panel-title">模型 Token 使用统计</h3>
          <span class="panel-sub">{{ rangeLabel }}</span>
        </div>
        <div ref="modelPieChart" class="chart-container"></div>
        <div v-if="topModels.length" class="trend-meta">
          <span v-for="(m, i) in topModels" :key="i"><b>{{ m.name }}</b>{{ m.label }}</span>
        </div>
      </div>
      <div class="panel">
        <div class="panel-head">
          <h3 class="panel-title">Token 使用趋势</h3>
          <span class="panel-sub">{{ rangeLabel }}</span>
        </div>
        <div ref="tokenTrendChart" class="chart-container"></div>
        <div v-if="tokenTrendMeta" class="trend-meta">
          <span><b>{{ tokenTrendMeta.peak }}</b>峰值</span>
          <span><b>{{ tokenTrendMeta.current }}</b>当前</span>
          <span><b>{{ tokenTrendMeta.cacheHit }}</b>缓存命中</span>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import * as echarts from 'echarts'

const COLORS = ['#4f86ff', '#32c8f5', '#34c98f', '#f4b74f', '#8b78ff', '#ef6d7a']

export default {
  name: 'MonitoringCenterPage',
  data() {
    return {
      range: '24h',
      summary: null,
      requestTrend: [],
      latencyDist: [],
      modelUsage: [],
      tokenTrend: [],
      charts: {},
      requestTrendMeta: null,
      tokenTrendMeta: null,
      latencySummary: null
    }
  },
  computed: {
    rangeLabel() {
      const map = { '24h': '近 24 小时', '7d': '近 7 天', '30d': '近 30 天' }
      return map[this.range] || '近 24 小时'
    },
    compareLabel() {
      const map = { '24h': '较昨日', '7d': '较上周', '30d': '较上月' }
      return map[this.range] || '较昨日'
    },
    rangeOptions() {
      return [
        { key: '24h', label: '24小时' },
        { key: '7d', label: '7天' },
        { key: '30d', label: '30天' }
      ]
    },
    topModels() {
      if (!this.modelUsage.length) return []
      return this.modelUsage.slice(0, 3).map((m, i) => ({
        name: m.model,
        label: 'TOP' + (i + 1)
      }))
    },
    summaryCards() {
      if (!this.summary) return []
      const s = this.summary
      return [
        {
          title: '请求总数',
          unit: '',
          value: this.$formatNumber(s.totalRequests || 0),
          changeText: this.pctText(s.requestsChange),
          changeClass: this.pctClass(s.requestsChange),
          changeArrow: this.pctArrow(s.requestsChange)
        },
        {
          title: '平均延迟',
          unit: 'ms',
          value: this.$formatNumber(Math.round(s.avgLatency || 0)),
          changeText: this.pctText(s.latencyChange),
          changeClass: this.pctClass(s.latencyChange),
          changeArrow: this.pctArrow(s.latencyChange)
        },
        {
          title: 'Token 使用',
          unit: '',
          value: this.$formatNumber(s.totalTokens || 0),
          changeText: this.pctText(s.tokensChange),
          changeClass: this.pctClass(s.tokensChange),
          changeArrow: this.pctArrow(s.tokensChange)
        },
        {
          title: '总成本',
          unit: '',
          value: '¥' + this.$formatNumber(s.totalCost || 0),
          changeText: this.pctText(s.costChange),
          changeClass: this.pctClass(s.costChange),
          changeArrow: this.pctArrow(s.costChange)
        }
      ]
    }
  },
  async mounted() {
    await this.fetchData()
    this.$nextTick(() => this.renderAllCharts())
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    Object.values(this.charts).forEach(c => c && c.dispose())
  },
  methods: {
    async fetchData() {
      try {
        const res = await this.$http.get('/admin/monitoring', { params: { range: this.range } })
        this.summary = res.summary
        this.requestTrend = res.requestTrend || []
        this.latencyDist = res.latencyDistribution || []
        this.modelUsage = res.modelUsage || []
        this.tokenTrend = res.tokenTrend || []
        this.computeMeta()
      } catch (e) {
        this.$message.error('加载监控数据失败')
      }
    },
    computeMeta() {
      // request trend meta
      if (this.requestTrend.length) {
        const vals = this.requestTrend.map(d => d.value)
        const peak = Math.max(...vals)
        const current = vals[vals.length - 1]
        const change = this.summary?.requestsChange
        this.requestTrendMeta = {
          peak: peak >= 100000000 ? (peak / 100000000).toFixed(1) + '亿' : (peak >= 10000 ? (peak / 10000).toFixed(1) + '万' : peak.toLocaleString()),
          current: current >= 100000000 ? (current / 100000000).toFixed(1) + '亿' : (current >= 10000 ? (current / 10000).toFixed(1) + '万' : current.toLocaleString()),
          change: (change >= 0 ? '+' : '') + (change || 0).toFixed(1) + '%'
        }
      }
      // token trend meta
      if (this.tokenTrend.length) {
        const vals = this.tokenTrend.map(d => d.value)
        const peak = Math.max(...vals)
        const current = vals[vals.length - 1]
        this.tokenTrendMeta = {
          peak: peak >= 100000000 ? (peak / 100000000).toFixed(1) + '亿' : (peak >= 10000 ? (peak / 10000).toFixed(1) + '万' : peak.toLocaleString()),
          current: current >= 100000000 ? (current / 100000000).toFixed(1) + '亿' : (current >= 10000 ? (current / 10000).toFixed(1) + '万' : current.toLocaleString()),
          cacheHit: this.summary?.cacheHitRate ? (this.summary.cacheHitRate * 100).toFixed(1) + '%' : '-'
        }
      }
      // latency summary
      if (this.latencyDist.length) {
        const total = this.latencyDist.reduce((s, d) => s + Number(d.count || 0), 0)
        const main = this.latencyDist[0]
        const timeout = this.latencyDist.find(d => d.label.includes('超时') || d.warn)
        const slowTotal = this.latencyDist.filter((d, i) => i > 0 && !d.warn).reduce((s, d) => s + Number(d.count || 0), 0)
        this.latencySummary = {
          mainLabel: main ? main.label : '-',
          mainPct: main ? (Number(main.count) / total * 100).toFixed(1) + '%' : '-',
          slow: slowTotal.toLocaleString(),
          timeout: Number(timeout?.count || 0)
        }
      }
    },
    async switchRange(key) {
      this.range = key
      Object.values(this.charts).forEach(c => c && c.dispose())
      this.charts = {}
      this.requestTrendMeta = null
      this.tokenTrendMeta = null
      this.latencySummary = null
      await this.fetchData()
      this.$nextTick(() => this.renderAllCharts())
    },
    handleResize() {
      Object.values(this.charts).forEach(c => c && c.resize())
      // 窄屏切换时重新渲染饼图布局
      const el = this.$refs.modelPieChart
      if (el) {
        const wasNarrow = el._donutNarrow
        const nowNarrow = el.clientWidth < 500
        if (wasNarrow !== nowNarrow) {
          el._donutNarrow = nowNarrow
          this.renderDonutChart('modelPieChart', this.modelUsage)
        }
      }
    },
    renderAllCharts() {
      this.renderLineChart('requestTrendChart', this.requestTrend, { color: '#4f86ff', color2: '#32c8f5', color3: '#8b78ff' })
      this.renderLatencyChart('latencyChart', this.latencyDist)
      this.renderDonutChart('modelPieChart', this.modelUsage)
      this.renderLineChart('tokenTrendChart', this.tokenTrend, { color: '#8b78ff', color2: '#32c8f5', color3: '#8b78ff' })
    },
    renderLineChart(refKey, data, palette) {
      const el = this.$refs[refKey]
      if (!el) return
      if (this.charts[refKey]) this.charts[refKey].dispose()
      if (!data.length) {
        el.innerHTML = '<div style="height:260px;display:grid;place-items:center;color:var(--muted);font-size:13px;">暂无数据</div>'
        return
      }
      const chart = echarts.init(el)
      this.charts[refKey] = chart
      const vals = data.map(d => d.value)
      const peakIdx = vals.indexOf(Math.max(...vals))
      const times = data.map(d => d.time)

      chart.setOption({
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(17,27,42,0.94)',
          borderColor: 'rgba(50,200,245,0.32)',
          textStyle: { color: '#eaf3ff', fontSize: 12 }
        },
        grid: { left: 16, right: 16, top: 16, bottom: 24 },
        xAxis: {
          type: 'category',
          data: times,
          axisLabel: { fontSize: 11, color: '#7f93ad', interval: 'auto' },
          axisLine: { lineStyle: { color: 'rgba(151,171,207,0.18)' } },
          axisTick: { show: false }
        },
        yAxis: {
          type: 'value',
          splitLine: { lineStyle: { type: 'dashed', color: 'rgba(151,171,207,0.10)' } },
          axisLabel: { show: false },
          axisLine: { show: false },
          axisTick: { show: false }
        },
        series: [{
          type: 'line',
          data: vals,
          smooth: true,
          lineStyle: { width: 2.4, color: palette.color2 },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: palette.color2.replace(')', ',0.28)').replace('rgb', 'rgba') },
              { offset: 1, color: palette.color2.replace(')', ',0)').replace('rgb', 'rgba') }
            ])
          },
          symbol: 'none',
          markPoint: {
            data: [{
              coord: [times[peakIdx], vals[peakIdx]],
              symbol: 'circle',
              symbolSize: 8,
              itemStyle: { color: palette.color, borderColor: '#101927', borderWidth: 2.5 },
              label: {
                show: true,
                position: 'top',
                formatter: this.formatChartValue(vals[peakIdx]),
                color: palette.color,
                fontSize: 11,
                fontWeight: 900
              }
            }, {
              coord: [times[times.length - 1], vals[vals.length - 1]],
              symbol: 'circle',
              symbolSize: 8,
              itemStyle: { color: '#eaf3ff', borderColor: palette.color, borderWidth: 2.5 },
              label: {
                show: true,
                position: 'top',
                formatter: this.formatChartValue(vals[vals.length - 1]),
                color: '#eaf3ff',
                fontSize: 11,
                fontWeight: 900
              }
            }]
          }
        }]
      })
    },
    renderLatencyChart(refKey, data) {
      const el = this.$refs[refKey]
      if (!el) return
      if (this.charts[refKey]) this.charts[refKey].dispose()
      if (!data.length) {
        el.innerHTML = '<div style="height:260px;display:grid;place-items:center;color:var(--muted);font-size:13px;">暂无数据</div>'
        return
      }
      const chart = echarts.init(el)
      this.charts[refKey] = chart
      const total = data.reduce((s, d) => s + Number(d.count || 0), 0)

      chart.setOption({
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(17,27,42,0.94)',
          borderColor: 'rgba(50,200,245,0.32)',
          textStyle: { color: '#eaf3ff', fontSize: 12 },
          formatter: p => {
            const d = data[p[0].dataIndex]
            const cnt = Number(d.count || 0)
            return `${d.label}<br/>${cnt.toLocaleString()} 次 (${(cnt / total * 100).toFixed(1)}%)`
          }
        },
        grid: { left: 80, right: 70, top: 10, bottom: 10 },
        xAxis: {
          type: 'value',
          splitLine: { show: false },
          axisLabel: { show: false },
          axisLine: { show: false },
          axisTick: { show: false }
        },
        yAxis: {
          type: 'category',
          data: data.map(d => d.label),
          axisLabel: { fontSize: 12, fontWeight: 700, color: '#a7b8cf' },
          axisLine: { show: false },
          axisTick: { show: false }
        },
        series: [{
          type: 'bar',
          data: data.map((d, i) => ({
            value: Number(d.count),
            itemStyle: {
              borderRadius: [0, 6, 6, 0],
              color: d.warn
                ? new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                    { offset: 0, color: 'rgba(255,176,32,0.9)' },
                    { offset: 1, color: 'rgba(239,109,122,0.78)' }
                  ])
                : new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                    { offset: 0, color: '#4f86ff' },
                    { offset: 1, color: '#32c8f5' }
                  ])
            }
          })),
          barWidth: 22,
          barMaxWidth: 22,
          label: {
            show: true,
            position: 'right',
            formatter: p => `${p.value.toLocaleString()}  ${(p.value / total * 100).toFixed(1)}%`,
            color: '#eaf3ff',
            fontSize: 12,
            fontWeight: 900
          }
        }]
      })
    },
    renderDonutChart(refKey, data) {
      const el = this.$refs[refKey]
      if (!el) return
      if (this.charts[refKey]) this.charts[refKey].dispose()
      if (!data.length) {
        el.innerHTML = '<div style="height:260px;display:grid;place-items:center;color:var(--muted);font-size:13px;">暂无数据</div>'
        return
      }
      // Top 5 + "其他"
      const sorted = [...data].sort((a, b) => Number(b.tokens) - Number(a.tokens))
      const top5 = sorted.slice(0, 5)
      const rest = sorted.slice(5)
      if (rest.length > 0) {
        const otherTokens = rest.reduce((s, d) => s + Number(d.tokens || 0), 0)
        top5.push({ model: '其他', tokens: otherTokens })
      }
      const chartData = top5
      const chart = echarts.init(el)
      this.charts[refKey] = chart
      const total = chartData.reduce((s, d) => s + Number(d.tokens || 0), 0)
      const isNarrow = el.clientWidth < 500
      el._donutNarrow = isNarrow

      chart.setOption({
        tooltip: {
          backgroundColor: 'rgba(17,27,42,0.94)',
          borderColor: 'rgba(50,200,245,0.32)',
          textStyle: { color: '#eaf3ff', fontSize: 12 },
          formatter: p => `${p.name}: ${this.formatChartValue(p.value)} token (${p.percent}%)`
        },
        legend: {
          orient: isNarrow ? 'horizontal' : 'vertical',
          left: isNarrow ? 'center' : undefined,
          right: isNarrow ? undefined : 10,
          top: isNarrow ? 'bottom' : 'center',
          bottom: isNarrow ? 0 : undefined,
          textStyle: { fontSize: isNarrow ? 10 : 12, color: '#c7d6ec' },
          icon: 'circle',
          itemWidth: 10,
          itemHeight: 10,
          formatter: name => {
            const item = chartData.find(d => d.model === name)
            const pct = item ? (Number(item.tokens) / total * 100).toFixed(1) : 0
            return `${name}  ${pct}%`
          }
        },
        series: [{
          type: 'pie',
          radius: isNarrow ? ['30%', '50%'] : ['42%', '66%'],
          center: isNarrow ? ['50%', '45%'] : ['40%', '50%'],
          avoidLabelOverlap: false,
          label: { show: false },
          emphasis: {
            label: { show: true, fontSize: 14, fontWeight: 700, color: '#eaf3ff' },
            itemStyle: { shadowBlur: 14, shadowColor: 'rgba(50,200,245,0.32)' }
          },
          data: chartData.map((d, i) => ({
            name: d.model,
            value: Number(d.tokens),
            itemStyle: { color: COLORS[i % COLORS.length] }
          }))
        }]
      })
    },
    exportSnapshot() {
      const el = this.$el
      if (!el) return
      this.$message.info('正在生成截图...')
      import('html2canvas').then(html2canvas => {
        html2canvas.default(el, {
          backgroundColor: '#0f1724',
          scale: 2,
          useCORS: true,
          logging: false
        }).then(canvas => {
          const link = document.createElement('a')
          link.download = `监控快照_${new Date().toISOString().slice(0, 19).replace(/[:-]/g, '')}.png`
          link.href = canvas.toDataURL('image/png')
          link.click()
          this.$message.success('截图已导出')
        }).catch(() => {
          this.$message.error('截图生成失败')
        })
      })
    },
    formatChartValue(v) {
      if (v == null) return '0'
      if (v >= 100000000) return (v / 100000000).toFixed(1) + '亿'
      if (v >= 10000) return (v / 10000).toFixed(1) + '万'
      return v.toLocaleString()
    },
        pctText(v) {
      const val = v || 0
      return (val >= 0 ? '+' : '') + val.toFixed(1) + '%'
    },
    pctClass(v) {
      const val = v || 0
      return val >= 0 ? 'up' : 'down'
    },
    pctArrow(v) {
      return (v || 0) >= 0 ? '↑' : '↓'
    }
  }
}
</script>

<style scoped>
.monitoring-page {
  padding: 0 24px 16px;
}

.toolbar {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.btn {
  height: 36px;
  padding: 0 13px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: rgba(255, 255, 255, 0.06);
  color: #dbe8fb;
  font: inherit;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.btn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(50, 200, 245, 0.42);
  color: #fff;
}

.btn.primary {
  border-color: rgba(50, 200, 245, 0.34);
  background: linear-gradient(135deg, #4f86ff, #32bde8);
  color: #fff;
  box-shadow: 0 10px 28px rgba(50, 150, 245, 0.18);
}

.grid {
  display: grid;
  gap: 16px;
}

.kpis.four {
  grid-template-columns: repeat(4, minmax(170px, 1fr));
}

.card {
  min-height: 116px;
  padding: 16px;
  position: relative;
  overflow: hidden;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: linear-gradient(180deg, rgba(27, 39, 58, 0.9), rgba(20, 30, 46, 0.88));
  box-shadow: var(--shadow);
}

.card::after {
  content: "";
  position: absolute;
  inset: 0 0 auto 0;
  height: 2px;
  background: linear-gradient(90deg, rgba(50, 200, 245, 0.78), transparent 70%);
  opacity: 0.5;
}

.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--muted);
  font-size: 13px;
  font-weight: 760;
}

.card-head span:last-child {
  color: #55f0b3;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.04em;
}

.metric {
  margin-top: 10px;
  display: flex;
  align-items: baseline;
  gap: 8px;
  max-width: 100%;
  font-size: clamp(20px, 2.2vw, 30px);
  line-height: 1.05;
  font-weight: 900;
  color: var(--text);
  overflow-wrap: anywhere;
  word-break: break-word;
}

.metric small {
  color: var(--muted);
  font-size: 13px;
  font-weight: 800;
}

.delta {
  margin-top: 12px;
  font-size: 12px;
  font-weight: 850;
}

.delta.up { color: #ff93a4; }
.delta.down { color: #55f0b3; }

.two {
  grid-template-columns: 1fr 1fr;
  margin-top: 16px;
}

.panel {
  padding: 18px;
  min-height: 220px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: linear-gradient(180deg, rgba(27, 39, 58, 0.9), rgba(20, 30, 46, 0.88));
  box-shadow: var(--shadow);
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.panel-title {
  margin: 0;
  font-size: 17px;
  font-weight: 880;
  color: var(--text);
}

.panel-sub {
  color: var(--muted);
  font-size: 12px;
}

.chart-container {
  width: 100%;
  height: 200px;
}

.trend-meta {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-top: 12px;
}

.trend-meta span {
  padding: 9px 10px;
  border-radius: 8px;
  color: #b8c8de;
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(133, 164, 255, 0.12);
  font-size: 11px;
  font-weight: 850;
}

.trend-meta b {
  display: block;
  margin-bottom: 3px;
  color: #eaf3ff;
  font-size: 13px;
}

.latency-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-top: 12px;
}

.latency-summary span {
  padding: 9px 10px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(133, 164, 255, 0.12);
  color: #b8c8de;
  font-size: 11px;
  font-weight: 850;
}

.latency-summary b {
  display: block;
  margin-bottom: 3px;
  color: #eaf3ff;
  font-size: 13px;
}

@media (max-width: 1100px) {
  .kpis.four { grid-template-columns: repeat(2, 1fr); }
  .two { grid-template-columns: 1fr; }
}

@media (max-width: 720px) {
  .kpis.four { grid-template-columns: 1fr; }
  .monitoring-page {
    padding: 0 12px 16px;
  }
}
</style>
